import type { ReactNode } from 'react'
import {
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
  DropdownScroll,
} from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { FilterSearchBar, FilterViewHeader } from '@/components/FilterControls'

export interface SelectFilterViewOption<TValue> {
  value: TValue
  title: string
  description?: string
  isSelected: boolean
}

interface SelectFilterViewProps {
  title: string
  onClear: () => void
  onSelectAll?: () => void
  /**
   * Shows the search bar when set.
   */
  onSearchChange?: (text: string) => void
  /**
   * The options, or the state of loading them.
   */
  children?: ReactNode
  testId?: string
}

/**
 * Editor for a filter that picks values from a list of options.
 */
export function SelectFilterView({
  title,
  onClear,
  onSelectAll,
  onSearchChange,
  children,
  testId,
}: SelectFilterViewProps) {
  return (
    <>
      <FilterViewHeader
        title={title}
        testId={testId && `${testId}-clear-button`}
        onClear={onClear}
        onSelectAll={onSelectAll}
      />
      {onSearchChange && <FilterSearchBar onChange={onSearchChange} />}
      <DropdownScroll>{children}</DropdownScroll>
    </>
  )
}

interface SelectFilterViewOptionsProps<TValue> {
  options: SelectFilterViewOption<TValue>[]
  onOptionPress: (value: TValue) => void
  testId?: string
}

/**
 * The options of a {@link SelectFilterView}, each with a checkbox.
 */
export function SelectFilterViewOptions<TValue>({
  options,
  onOptionPress,
  testId,
}: SelectFilterViewOptionsProps<TValue>) {
  return options.map((c, index) => (
    <DropdownItem
      key={`${c.title}-${index}`}
      testId={testId && `${testId}-${c.title}`}
      onPress={() => onOptionPress(c.value)}
      icon={
        <Icon
          color="var(--theme-base)"
          name={c.isSelected ? 'checkbox' : 'checkbox-blank'}
        />
      }
    >
      <DropdownItemTitle>{c.title}</DropdownItemTitle>
      <DropdownItemDescription>{c.description}</DropdownItemDescription>
    </DropdownItem>
  ))
}
